import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Autocomplete,
  Divider,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../contexts/AuthContext';
import { universalModalPaperProps, universalModalBackdropProps } from '../styles/modalStyles';
import axios from 'axios';

interface PrerequisiteRecord {
  Id: string;
  Name: string;
  Course_Name__c: string;
  Prerequisite_Award__c: string;
  Can_Be_Expired__c: boolean;
  Validity_Period__c: number | null;
  Minimum_Age__c: number | null;
  Prerequisite_Order__c: number | null;
  Logic_Formula__c: string | null;
}

interface CoursePrerequisites {
  courseName: string;
  prerequisites: PrerequisiteRecord[];
  logicFormula: string;
}

interface GroupedPrerequisites {
  [courseName: string]: PrerequisiteRecord[];
}

const PrerequisiteManagementFormula: React.FC = () => {
  const { user } = useAuth();
  const [prerequisites, setPrerequisites] = useState<GroupedPrerequisites>({});
  const [availableAwards, setAvailableAwards] = useState<string[]>([]);
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CoursePrerequisites | null>(null);

  // Fetch prerequisites
  const fetchPrerequisites = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/prerequisites');
      
      // Group prerequisites by course name
      const grouped: GroupedPrerequisites = {};
      response.data.forEach((prereq: PrerequisiteRecord) => {
        const courseName = prereq.Course_Name__c;
        if (!grouped[courseName]) {
          grouped[courseName] = [];
        }
        grouped[courseName].push(prereq);
      });
      
      // Sort prerequisites within each course by order
      Object.keys(grouped).forEach(courseName => {
        grouped[courseName].sort((a, b) => 
          (a.Prerequisite_Order__c || 0) - (b.Prerequisite_Order__c || 0)
        );
      });
      
      setPrerequisites(grouped);
    } catch (err) {
      setError('Failed to fetch prerequisites');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available awards for autocomplete
  const fetchAvailableAwards = async () => {
    try {
      const response = await axios.get('/api/prerequisites/available-awards');
      setAvailableAwards(response.data.awards || []);
    } catch (err) {
      console.error('Failed to fetch available awards:', err);
    }
  };

  // Fetch available course types for autocomplete
  const fetchAvailableCourses = async () => {
    try {
      const response = await axios.get('/api/prerequisites/available-courses');
      setAvailableCourses(response.data.courses || []);
    } catch (err) {
      console.error('Failed to fetch available courses:', err);
      // Fallback course list
      const fallbackCourses = [
        'Bronze Cross',
        'Bronze Medallion', 
        'Bronze Star',
        'Bronze Combo',
        'National Lifeguard',
        'Standard First Aid with CPR-C',
        'Emergency First Aid with CPR-B'
      ];
      setAvailableCourses(fallbackCourses);
    }
  };

  useEffect(() => {
    fetchPrerequisites();
    fetchAvailableAwards();
    fetchAvailableCourses();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleOpenDialog = (courseName?: string) => {
    if (courseName && prerequisites[courseName]) {
      // Edit existing course
      const coursePrereqs = prerequisites[courseName];
      const formula = coursePrereqs[0]?.Logic_Formula__c || '';
      
      setEditingCourse({
        courseName,
        prerequisites: [...coursePrereqs],
        logicFormula: formula
      });
    } else {
      // Create new course
      setEditingCourse({
        courseName: courseName || '',
        prerequisites: [],
        logicFormula: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
  };

  const addPrerequisite = () => {
    if (!editingCourse) return;
    
    const newOrder = editingCourse.prerequisites.length + 1;
    const newPrereq: PrerequisiteRecord = {
      Id: `new-${Date.now()}`, // Temporary ID
      Name: editingCourse.courseName,
      Course_Name__c: editingCourse.courseName,
      Prerequisite_Award__c: '',
      Can_Be_Expired__c: false,
      Validity_Period__c: null,
      Minimum_Age__c: null,
      Prerequisite_Order__c: newOrder,
      Logic_Formula__c: editingCourse.logicFormula
    };
    
    setEditingCourse({
      ...editingCourse,
      prerequisites: [...editingCourse.prerequisites, newPrereq]
    });
  };

  const removePrerequisite = (index: number) => {
    if (!editingCourse) return;
    
    const updated = editingCourse.prerequisites.filter((_, i) => i !== index);
    // Renumber prerequisites
    updated.forEach((prereq, i) => {
      prereq.Prerequisite_Order__c = i + 1;
    });
    
    setEditingCourse({
      ...editingCourse,
      prerequisites: updated
    });
  };

  const updatePrerequisite = (index: number, field: keyof PrerequisiteRecord, value: any) => {
    if (!editingCourse) return;
    
    const updated = [...editingCourse.prerequisites];
    updated[index] = { ...updated[index], [field]: value };
    
    setEditingCourse({
      ...editingCourse,
      prerequisites: updated
    });
  };

  const validateFormula = (formula: string, prerequisiteCount: number): { valid: boolean; error?: string } => {
    if (!formula.trim()) {
      if (prerequisiteCount <= 1) return { valid: true };
      return { valid: false, error: 'Formula required for multiple prerequisites' };
    }
    
    // Check for valid numbers
    const numbers = formula.match(/\d+/g) || [];
    const invalidNumbers = numbers.filter(num => parseInt(num) > prerequisiteCount || parseInt(num) < 1);
    
    if (invalidNumbers.length > 0) {
      return { valid: false, error: `Invalid prerequisite numbers: ${invalidNumbers.join(', ')}` };
    }
    
    // Basic syntax check
    const validPattern = /^[\d\s\(\)ANDOR]+$/i;
    if (!validPattern.test(formula)) {
      return { valid: false, error: 'Formula can only contain numbers, AND, OR, and parentheses' };
    }
    
    return { valid: true };
  };

  const handleSave = async () => {
    if (!editingCourse) return;
    
    try {
      // Validate formula
      const validation = validateFormula(editingCourse.logicFormula, editingCourse.prerequisites.length);
      if (!validation.valid) {
        setError(validation.error || 'Invalid formula');
        return;
      }
      
      // Save each prerequisite
      for (const prereq of editingCourse.prerequisites) {
        const dataToSave = {
          Name: editingCourse.courseName,
          Course_Name__c: editingCourse.courseName,
          Prerequisite_Award__c: prereq.Prerequisite_Award__c || '',
          Can_Be_Expired__c: prereq.Can_Be_Expired__c,
          Validity_Period__c: prereq.Validity_Period__c || 0,
          Minimum_Age__c: prereq.Minimum_Age__c || 0,
          Prerequisite_Order__c: prereq.Prerequisite_Order__c || 1,
          Logic_Formula__c: editingCourse.logicFormula || ''
        };
        
        if (prereq.Id.startsWith('new-')) {
          // Create new
          await axios.post('/api/prerequisites', dataToSave);
        } else {
          // Update existing
          await axios.put(`/api/prerequisites/${prereq.Id}`, dataToSave);
        }
      }
      
      setSuccess('Prerequisites saved successfully');
      handleCloseDialog();
      fetchPrerequisites();
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save prerequisites');
      console.error(err);
    }
  };

  const handleDelete = async (prereqId: string) => {
    if (window.confirm('Are you sure you want to delete this prerequisite?')) {
      try {
        await axios.delete(`/api/prerequisites/${prereqId}`);
        setSuccess('Prerequisite deleted successfully');
        fetchPrerequisites();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete prerequisite');
        console.error(err);
      }
    }
  };

  const renderPrerequisiteFormula = (coursePrereqs: PrerequisiteRecord[]): React.ReactNode => {
    if (coursePrereqs.length === 0) return null;
    if (coursePrereqs.length === 1) {
      const prereq = coursePrereqs[0];
      const parts = [];
      if (prereq.Prerequisite_Award__c) parts.push(prereq.Prerequisite_Award__c);
      if (prereq.Minimum_Age__c) parts.push(`age ${prereq.Minimum_Age__c}+`);
      return parts.join(' + ');
    }
    
    const formula = coursePrereqs[0]?.Logic_Formula__c || '';
    if (!formula) {
      // Default to simple listing
      return coursePrereqs.map((prereq, index) => {
        const parts = [];
        if (prereq.Prerequisite_Award__c) parts.push(prereq.Prerequisite_Award__c);
        if (prereq.Minimum_Age__c) parts.push(`age ${prereq.Minimum_Age__c}+`);
        return `${index + 1}. ${parts.join(' + ')}`;
      }).join(' AND ');
    }
    
    // Replace numbers with actual prerequisite descriptions
    let displayFormula = formula;
    coursePrereqs.forEach((prereq, index) => {
      const number = (index + 1).toString();
      const parts = [];
      if (prereq.Prerequisite_Award__c) parts.push(prereq.Prerequisite_Award__c);
      if (prereq.Minimum_Age__c) parts.push(`age ${prereq.Minimum_Age__c}+`);
      const description = parts.join(' + ') || 'Empty';
      displayFormula = displayFormula.replace(new RegExp(`\\b${number}\\b`, 'g'), `(${description})`);
    });
    
    return displayFormula;
  };

  if (!user || user.role !== 'admin') {
    return (
      <Container>
        <Alert severity="error">You must be an admin to access this page.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Prerequisite Management (Formula-Based)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Course Prerequisites
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%', minWidth: '150px' }}>Course</TableCell>
                <TableCell sx={{ width: '65%', minWidth: '300px' }}>Prerequisites Logic</TableCell>
                <TableCell sx={{ width: '15%', minWidth: '120px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(prerequisites).map(([courseName, coursePrereqs]) => (
                <TableRow key={courseName}>
                  <TableCell sx={{ 
                    verticalAlign: 'top', 
                    fontWeight: 'bold',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                    {courseName}
                  </TableCell>
                  <TableCell sx={{ 
                    verticalAlign: 'top',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: 0 // This forces the cell to use the width constraint
                  }}>
                    <Typography variant="body2" sx={{ 
                      mb: 1,
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere'
                    }}>
                      {renderPrerequisiteFormula(coursePrereqs)}
                    </Typography>
                    {coursePrereqs.length > 1 && coursePrereqs[0]?.Logic_Formula__c && (
                      <Chip
                        label={`Formula: ${coursePrereqs[0].Logic_Formula__c}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ 
                          maxWidth: '100%',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ 
                    verticalAlign: 'top',
                    minWidth: '120px'
                  }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(courseName)}
                      variant="outlined"
                      sx={{ 
                        minWidth: '80px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>
          {editingCourse?.courseName ? `Edit Prerequisites: ${editingCourse.courseName}` : 'Add Course Prerequisites'}
        </DialogTitle>
        <DialogContent>
          {editingCourse && (
            <Box sx={{ pt: 2 }}>
              <Autocomplete
                fullWidth
                options={availableCourses}
                value={editingCourse.courseName}
                onChange={(_, newValue) => setEditingCourse({ ...editingCourse, courseName: newValue || '' })}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Course Name"
                    required
                    helperText="Select from existing courses or type a new course name"
                  />
                )}
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Prerequisites
              </Typography>

              <List>
                {editingCourse.prerequisites.map((prereq, index) => (
                  <ListItem key={index} sx={{ border: '1px solid #ddd', borderRadius: 1, mb: 1 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ mr: 2, minWidth: '30px' }}>
                          {index + 1}.
                        </Typography>
                        <Autocomplete
                          options={availableAwards}
                          value={prereq.Prerequisite_Award__c}
                          onChange={(_, newValue) => updatePrerequisite(index, 'Prerequisite_Award__c', newValue || '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Award"
                              variant="outlined"
                              size="small"
                            />
                          )}
                          sx={{ flexGrow: 1, mr: 2 }}
                        />
                        <IconButton
                          onClick={() => removePrerequisite(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, ml: 4 }}>
                        <TextField
                          label="Minimum Age"
                          type="text"
                          size="small"
                          value={prereq.Minimum_Age__c || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d+$/.test(value)) {
                              updatePrerequisite(index, 'Minimum_Age__c', value === '' ? null : Number(value));
                            }
                          }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          label="Validity (Years)"
                          type="text"
                          size="small"
                          value={prereq.Validity_Period__c || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d+$/.test(value)) {
                              updatePrerequisite(index, 'Validity_Period__c', value === '' ? null : Number(value));
                            }
                          }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          sx={{ width: 120 }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={prereq.Can_Be_Expired__c}
                              onChange={(e) => updatePrerequisite(index, 'Can_Be_Expired__c', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Can Be Expired"
                        />
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>

              <Button
                startIcon={<AddIcon />}
                onClick={addPrerequisite}
                variant="outlined"
                sx={{ mb: 3 }}
              >
                Add Prerequisite
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Logic Formula
              </Typography>
              <TextField
                fullWidth
                label="Logic Formula"
                value={editingCourse.logicFormula}
                onChange={(e) => setEditingCourse({ ...editingCourse, logicFormula: e.target.value })}
                placeholder="e.g., 1 AND (2 OR 3), 1 OR 2, 1 AND 2 AND 3"
                helperText="Use prerequisite numbers with AND, OR, and parentheses. Leave blank for single prerequisites."
                multiline
                rows={2}
              />

              {editingCourse.prerequisites.length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Preview:
                  </Typography>
                  <Typography variant="body2">
                    {renderPrerequisiteFormula(editingCourse.prerequisites)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Prerequisites
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrerequisiteManagementFormula;
